/**
 * shadcn/ui Dialog (Radix) on the brand tokens. Author: Mahmoud Ragab Ahmed.
 *
 * The content carries `q-ui` because it portals to <body>, outside any migrated region:
 * the scoped preflight (brand/ui.css) must reach it there too.
 */
import { XIcon } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";

import { cn } from "@/lib/utils";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>): React.JSX.Element {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "q-ui fixed inset-0 z-60 bg-scrim backdrop-blur-[2px]",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        className,
      )}
      {...props}
    />
  );
}

export function DialogContent({
  className,
  children,
  closeLabel,
  closeTestId,
  overlayTestId,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  readonly closeLabel: string;
  readonly closeTestId?: string;
  readonly overlayTestId?: string;
}): React.JSX.Element {
  return (
    <DialogPortal>
      <DialogOverlay data-testid={overlayTestId} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "q-ui fixed start-1/2 top-[4vh] z-60 flex max-h-[92vh] w-[calc(100%-1.5rem)] max-w-[34rem] -translate-x-1/2 flex-col",
          "overflow-y-auto rounded-md border border-rule-strong bg-background text-foreground shadow-2xl",
          "p-(--q-s1) gap-(--q-s0)",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-2",
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close
          data-slot="dialog-close"
          data-testid={closeTestId}
          aria-label={closeLabel}
          className="absolute end-3 top-3 inline-flex size-11 items-center justify-center rounded-md border border-transparent text-muted-foreground hover:border-rule-strong hover:text-foreground"
        >
          <XIcon className="size-5" aria-hidden="true" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export function DialogHeader({
  className,
  ...props
}: React.ComponentProps<"div">): React.JSX.Element {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 pe-12", className)}
      {...props}
    />
  );
}

export function DialogFooter({
  className,
  ...props
}: React.ComponentProps<"div">): React.JSX.Element {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-wrap items-center justify-end gap-3 border-t border-rule pt-(--q-s0)",
        className,
      )}
      {...props}
    />
  );
}

export function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>): React.JSX.Element {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-xl leading-tight font-normal text-word", className)}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>): React.JSX.Element {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-base text-muted-foreground", className)}
      {...props}
    />
  );
}
